import { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import productTypeService from '../../services/productTypeService';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import CreateProductTypeModal from './CreateProductTypeModal';

const ProductTypesIndex = () => {
  const { get, isRtl } = useLocalization();
  const [productTypes, setProductTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadProductTypes();
  }, []);

  const loadProductTypes = async () => {
    try {
      setIsLoading(true);
      const data = await productTypeService.getAll();
      setProductTypes(data);
    } catch (error) {
      console.error('Error loading product types:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(isRtl ? 'هل أنت متأكد من حذف نوع المنتج هذا؟' : 'Are you sure you want to delete this product type?')) {
      try {
        await productTypeService.delete(id);
        loadProductTypes();
      } catch (error) {
        console.error('Error deleting product type:', error);
      }
    }
  };

  const filteredProductTypes = productTypes.filter(type => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      type.nameEn?.toLowerCase().includes(search) ||
      type.nameAr?.toLowerCase().includes(search)
    );
  });

  const columns = [
    {
      header: isRtl ? 'الأيقونة' : 'Icon',
      accessor: 'iconSvg',
      render: (value) => value ? (
        <div dangerouslySetInnerHTML={{ __html: value }} style={{ width: '24px', height: '24px' }} />
      ) : '-',
    },
    { header: isRtl ? 'الاسم (إنجليزي)' : 'Name (English)', accessor: 'nameEn' },
    { header: isRtl ? 'الاسم (عربي)' : 'Name (Arabic)', accessor: 'nameAr' },
    {
      header: isRtl ? 'الحالة' : 'Status',
      accessor: 'isActive',
      render: (value) => (
        <span className={`badge ${value ? 'badge-success' : 'badge-neutral'}`}>
          {value ? (isRtl ? 'نشط' : 'Active') : (isRtl ? 'غير نشط' : 'Inactive')}
        </span>
      ),
    },
    {
      header: isRtl ? 'الإجراءات' : 'Actions',
      accessor: 'id',
      render: (value) => (
        <Button size="small" variant="danger" onClick={() => handleDelete(value)}>
          {get('delete')}
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Loading product types..." />;
  }

  return (
    <div className="product-types-content" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{get('product_type_management')}</h1>
            <p className="page-description">{get('product_type_management_desc')}</p>
          </div>
          <div className="page-header-actions">
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
              </svg>
              {get('add_product_type')}
            </Button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-box" style={{ maxWidth: '300px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث...' : 'Search...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            data={filteredProductTypes}
            emptyMessage={isRtl ? 'لا توجد أنواع منتجات' : 'No product types found'}
          />
        </div>
      </div>

      {showCreateModal && (
        <CreateProductTypeModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadProductTypes();
          }}
        />
      )}
    </div>
  );
};

export default ProductTypesIndex;
